/**
 * Safely extracts the hostname from a URL string
 * @param url - The URL string to parse
 * @returns The hostname or the original string if parsing fails
 */
export const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  }
  catch {
    console.info("Invalid URL:", url);
    return url;
  }
};

export const stripProtocolAndWww = (url: string): string => {
  return url.replace(/^https?:\/\/(www\.)?/, "");
};

/**
 * Checks whether a URL is relative or points to the host page.
 */
export const isInternalUrl = (url: string): boolean => {
  try {
    if (!url.includes("://")) {
      return true;
    }

    return new URL(url).hostname === window.location.hostname;
  }
  catch {
    return false;
  }
};

/**
 * Checks if a URL points to a PDF file
 * @param url - The URL string to check
 * @returns True if the URL ends with .pdf (case insensitive)
 */
export const isPdfUrl = (url: string): boolean => {
  return url.toLowerCase().endsWith(".pdf");
};

/**
 * Safe URL transform for React Markdown that allows safe protocols
 * while blocking dangerous ones like javascript:, data:, vbscript:, file:
 * @param url - The URL to validate and transform
 * @returns The URL if safe, empty string if dangerous
 */
export const safeUrlTransform = (url: string): string => {
  // Safe protocols: http(s), mailto, tel
  const safeProtocolRegex = /^(?:https?|mailto|tel):/i;

  // Relative URLs have no leading scheme, or start with a path-like prefix.
  const relativeUrlRegex = /^(?:\/|[^:/?#]+\/)/;
  const hasProtocol = /^[a-z][a-z0-9+.-]*:/i.test(url);
  const isRelative = !hasProtocol || relativeUrlRegex.test(url);

  // Allow safe protocols and relative URLs
  if (isRelative || safeProtocolRegex.test(url)) {
    return url;
  }

  // Block dangerous protocols (javascript:, data:, vbscript:, file:, etc.)
  console.warn("Blocked potentially unsafe URL:", url);
  return "";
};
